import { useAppDispatch, useAppSelector } from "@/lib/store";
import { useEffect } from "react";
import {
  fetchVendorDetail,
  uploadDoc,
  deleteDoc,
  fetchVendorDocuments,
  setDocPage,
  setDocPageSize,
  fetchVendorProperties,
  addProperty,
  removeProperty,
  fetchAvailableProperties,
  setPropPage,
  setPropPageSize,
} from "./vendor-detail.slice";
import { uuid } from "@/interfaces";
import { toast } from "sonner";

const messageOf = (error: unknown, fallback: string): string => (error as { message?: string } | null)?.message ?? fallback;

export function useVendorDetail(vendorId?: uuid) {
  const dispatch = useAppDispatch();
  const {
    detail,
    loading,
    error,
    deletingDoc,
    documents,
    docLoading,
    docPage,
    docPageSize,
    docTotal,
    properties,
    propLoading,
    propPage,
    propPageSize,
    propTotal,
    availableProperties,
    addingProperty,
    removingProperty,
  } = useAppSelector((s) => s.vendorDetail);

  useEffect(() => {
    if (vendorId) {
      dispatch(fetchVendorDetail(vendorId));
      dispatch(fetchVendorDocuments({ vendorId, page: docPage, pageSize: docPageSize }));
      dispatch(fetchVendorProperties({ vendorId, page: propPage, pageSize: propPageSize }));
    }
  }, [dispatch, vendorId]);

  useEffect(() => {
    if (!vendorId) return;
    dispatch(fetchVendorDocuments({ vendorId, page: docPage, pageSize: docPageSize }));
  }, [dispatch, vendorId, docPage, docPageSize]);

  useEffect(() => {
    if (!vendorId) return;
    dispatch(fetchVendorProperties({ vendorId, page: propPage, pageSize: propPageSize }));
  }, [dispatch, vendorId, propPage, propPageSize]);

  return {
    detail,
    loading,
    error,
    deletingDoc,

    uploadDocument: async (file: File, expiresAt?: string | Date): Promise<boolean> => {
      if (!vendorId) return false;
      try {
        await dispatch(uploadDoc({ vendorId, file, expiresAt })).unwrap();
        toast.success("Document uploaded successfully.");
        return true;
      } catch (error) {
        toast.error(messageOf(error, "Failed to upload the document."));
        return false;
      } finally {
        dispatch(fetchVendorDocuments({ vendorId, page: docPage, pageSize: docPageSize }));
      }
    },

    deleteDocument: async (docId: uuid) => {
      if (!vendorId) return;
      try {
        await dispatch(deleteDoc(docId)).unwrap();
        toast.success("Document deleted successfully.");
      } catch (error) {
        toast.error(messageOf(error, "Failed to delete the document."));
      }
      dispatch(fetchVendorDocuments({ vendorId, page: docPage, pageSize: docPageSize }));
    },

    documents,
    docLoading,
    docPage,
    docPageSize,
    docTotal,
    setDocPage: (p: number) => dispatch(setDocPage(p)),
    setDocPageSize: (s: number) => dispatch(setDocPageSize(s)),

    properties,
    propLoading,
    propPage,
    propPageSize,
    propTotal,
    availableProperties,
    addingProperty,
    removingProperty,
    setPropPage: (p: number) => dispatch(setPropPage(p)),
    setPropPageSize: (s: number) => dispatch(setPropPageSize(s)),

    addPropertyToVendor: async (propertyId: uuid) => {
      if (!vendorId) return;
      try {
        await dispatch(addProperty({ vendorId, propertyId })).unwrap();
        toast.success("Property added successfully.");
      } catch (error) {
        toast.error(messageOf(error, "Failed to add the property."));
      }
      dispatch(fetchVendorProperties({ vendorId, page: propPage, pageSize: propPageSize }));
    },

    removeProperty: async (propertyId: uuid) => {
      if (!vendorId) return;
      try {
        await dispatch(removeProperty({ vendorId, propertyId })).unwrap();
        toast.success("Property removed successfully.");
      } catch (error) {
        toast.error(messageOf(error, "Failed to remove the property."));
      }
      dispatch(fetchVendorProperties({ vendorId, page: propPage, pageSize: propPageSize }));
    },

    loadAvailableProperties: async (search?: string) => {
      if (!vendorId) return;
      await dispatch(fetchAvailableProperties({ vendorId, search })).unwrap();
    },

    services: detail?.vendor.services ?? [],
  };
}
